import React from 'react';
import { Icon, TIconName } from '@unmind/design-system';
import { ParseKeys, TOptions } from 'i18next';
import { useTranslation } from 'react-i18next';

import './ProfileAttributes.scss';

const HIDDEN_VALUES = ['NotSay', 'Other'];

interface ProfileAttributesProps {
  country: string;
  languages: string[];
  gender: string | null;
  ethnicity: unknown;
}

interface AttributeProps {
  icon: TIconName;
  label: string;
  value: string | null;
}

const Attribute = ({ icon, label, value }: AttributeProps) =>
  value ? (
    <li className="item">
      <Icon
        className="icon"
        icon={icon}
        size="large"
        tone="default"
        aria-hidden
      />
      <span className="body-m text">{`${label}: ${value}`}</span>
    </li>
  ) : null;

export default ({
  country,
  languages,
  gender,
  ethnicity,
}: ProfileAttributesProps) => {
  const { t } = useTranslation('book', { keyPrefix: 'practitioner' });

  const translateValue = (group: 'genders' | 'ethnicities', value: string) =>
    HIDDEN_VALUES.includes(value)
      ? null
      : t(`${group}.${value}` as ParseKeys<'book', TOptions, 'practitioner'>, {
          defaultValue: value,
        });

  const ethnicities = Array.isArray(ethnicity)
    ? ethnicity
        .map(value => translateValue('ethnicities', value))
        .filter(Boolean)
    : [];

  return (
    <div className="practitioner-profile-attributes">
      <ul className="list">
        <Attribute
          icon="location"
          label={t('profile.location')}
          value={country}
        />
        <Attribute
          icon="language"
          label={t('profile.languages')}
          value={languages.join(', ')}
        />
        <Attribute
          icon="profile"
          label={t('profile.gender')}
          value={gender && translateValue('genders', gender)}
        />
        <Attribute
          icon="timezone"
          label={t('profile.ethnicity')}
          value={ethnicities.join('; ')}
        />
      </ul>
    </div>
  );
};
